import { MenuItem, Select, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Label,
} from 'recharts';
import { MenuProps } from '../../utils/constants';
import { getMonthlyFees } from '../../features/dashboard/dashboardSlice';
import { useDispatch } from 'react-redux';
import useNotification from '../../hooks/useNotification';
import ToastNotification from '../UIElements/ToastNotification';

const FeeCollectionProgress = ({ FeeData }) => {
  const dispatch = useDispatch();
  const [month, setMonth] = useState('');
  const [monthlyData, setMonthlyData] = useState('');
  const [feeDetails, setFeeDetails] = useState([
    {
      name: 'TotalCollected',
      value: 0,
    },
    {
      name: 'FeeWriteOff',
      value: 0,
    },
    {
      name: 'RemainingDue',
      value: 0,
    },
  ]);
  const { notification, showError, hideNotification } = useNotification();
  async function getMontlyList(param) {
    try {
      const res = await dispatch(getMonthlyFees(param)).unwrap();
      const list = Object.entries(res?.summary)?.map(([key, value]) => ({
        name: key,
        value: value,
      }));
      setMonthlyData({ ...res, summary: list });
    } catch (error) {
      console.log(error);
      showError('Error in getting class details.');
      setMonthlyData({
        month: 'Apr 2026',
        summary: [],
        total: 100,
      });
    }
  }
  useEffect(() => {
    const data = FeeData?.feeDetails?.filter((obj) => {
      if (obj.name !== 'TotalFee' && obj.name !== 'TotalDue') {
        return obj;
      }
    });
    setFeeDetails(data || []);
  }, [FeeData]);
  return (
    <div className='bg-white rounded-lg shadow-md  size-full '>
      <div className='px-3 pt-2 flex justify-between flex-wrap'>
        <Typography variant='h6'>Tution Fee Summary</Typography>
        <Select
          MenuProps={MenuProps}
          displayEmpty
          value={month}
          onChange={(e) => {
            const value = e.target.value;
            if (value) {
              setMonth(value);
              getMontlyList(value);
            } else {
              setMonth('');
            }
          }}
          labelId='class-label'
          sx={{
            maxWidth: 'fit',
            height: '24px',
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#ccc', // normal border color
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#ccc', // normal border color
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#ccc', // keep same on hover
            },
            '&.MuiSelect-select:focus': {
              backgroundColor: 'transparent', // remove blue background on focus
            },
          }}
        >
          {FeeData && FeeData?.monthlySummary?.length == 0 ? (
            <MenuItem value=''>No Payments</MenuItem>
          ) : (
            <MenuItem
              value=''
              className='text-blue-600 bg-blue-100 font-semibold'
            >
              Monthly Fees
            </MenuItem>
          )}
          {FeeData &&
            FeeData?.monthlySummary?.map((obj, index) => (
              <MenuItem
                key={obj.monthYear}
                value={obj.monthYear}
                className={`${index % 2 !== 0 ? 'bg-gray-100' : ''}`}
              >
                {obj.monthYear}
              </MenuItem>
            ))}
        </Select>
      </div>
      {month ? (
        <MonthlySummary monthlyDetails={monthlyData} />
      ) : (
        <OverallSummary feeDetails={feeDetails} />
      )}
      <ToastNotification
        open={notification.open}
        onClose={hideNotification}
        message={notification.message}
        title={notification.title}
        severity={notification.severity}
        autoHideDuration={notification.autoHideDuration}
        position={notification.position}
      />
    </div>
  );
};

export default FeeCollectionProgress;

const OverallSummary = ({ feeDetails = {} }) => {
  const COLORS = [
    '#3EC043', //green - totalCollected
    // '#3468FF', //blue - totalDue
    '#e80e48', //Red - feeWriteOff
    '#3468FF', //blue - remainingDue
  ];
  return (
    <>
      {!feeDetails?.every((obj) => obj.value == 0) ? (
        <div className='overflow-auto'>
          <div className='flex gap-2  min-w-[400px] justify-evenly items-center '>
            <div className='w-80 '>
              <ResponsiveContainer width='100%' height={230}>
                <PieChart>
                  <Pie
                    data={feeDetails}
                    dataKey='value'
                    nameKey='name'
                    cx='50%'
                    cy='50%'
                    margin={{ top: 10, bottom: 10, left: 0, right: 10 }}
                    innerRadius={50}
                    outerRadius={100}
                    minAngle={5}
                  >
                    {feeDetails?.map((entry, index) => (
                      <Cell key={index} fill={COLORS[index]} />
                    ))}
                    <Label
                      content={
                        <text
                          x='50%'
                          y='45%'
                          textAnchor='middle'
                          dominantBaseline='middle'
                          style={{ fontSize: '12px', fontWeight: 'bold' }}
                        >
                          <tspan
                            x='50%'
                            style={{ fontSize: '16px', marginBottom: '5px' }}
                          >
                            Total
                          </tspan>
                          <tspan x='50%' dy='20'>
                            {`₹${new Intl.NumberFormat('en-IN').format(feeDetails?.[0]?.value ?? '--')}`}
                          </tspan>
                        </text>
                      }
                    />
                  </Pie>
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload || !payload.length) return null;
                      const formatted = new Intl.NumberFormat('en-IN', {
                        style: 'currency',
                        currency: 'INR',
                      }).format(payload?.[0]?.value);

                      return (
                        <div className='bg-white border rounded shadow p-2 text-center min-w-[60px]'>
                          <div className='font-bold '>{payload?.[0]?.name}</div>
                          <div className='font-semibold text-black'>
                            {formatted}
                          </div>
                        </div>
                      );
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>{' '}
            </div>
            <div className='flex-1 flex flex-col mr-4 lg:mr-10 text-nowrap gap-2'>
              <div className='flex gap-2 justify-center '>
                <div className='w-5 h-3 rounded-full bg-[#3468FF] self-center '></div>{' '}
                <div>Remaining Due</div>
              </div>
              {feeDetails?.[1]?.value > 0 && (
                <div className='flex gap-2 justify-center pr-4'>
                  <div className='w-5 h-3 rounded-full bg-[#e80e48] self-center'></div>{' '}
                  <div>Fee WriteOff</div>
                </div>
              )}
              <div className='flex justify-center gap-2'>
                <div className='w-5 h-3 rounded-full bg-[#3EC043] self-center'></div>{' '}
                <div className=''>Total collected</div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className=' text-center mt-20 mb-24 w-full'>No Fee Details</div>
      )}
    </>
  );
};
const MonthlySummary = ({ monthlyDetails = {} }) => {
  const COLORS = [
    '#3EC043', // UPI
    '#3468FF', // Online
    '#e80e48', // Card
  ];

  return (
    <>
      {monthlyDetails?.summary?.length > 0 ? (
        <div className='overflow-auto'>
          <div className='flex gap-2  min-w-[400px] justify-evenly items-center'>
            <div className='w-80 '>
              <ResponsiveContainer width='100%' height={230}>
                <PieChart>
                  <Pie
                    data={monthlyDetails?.summary}
                    dataKey='value'
                    nameKey='name'
                    cx='50%'
                    cy='50%'
                    innerRadius={50}
                    outerRadius={100}
                    minAngle={5}
                  >
                    {monthlyDetails?.summary?.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}

                    <Label
                      content={
                        <text
                          x='50%'
                          y='45%'
                          textAnchor='middle'
                          dominantBaseline='middle'
                          style={{
                            fontSize: '12px',
                            fontWeight: 'bold',
                          }}
                        >
                          <tspan
                            x='50%'
                            style={{
                              fontSize: '16px',
                            }}
                          >
                            Total
                          </tspan>

                          <tspan x='50%' dy='20'>
                            {`₹${new Intl.NumberFormat('en-IN').format(
                              monthlyDetails?.total,
                            )}`}
                          </tspan>
                        </text>
                      }
                    />
                  </Pie>

                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload || !payload.length) return null;

                      const formatted = new Intl.NumberFormat('en-IN', {
                        style: 'currency',
                        currency: 'INR',
                      }).format(payload?.[0]?.value);

                      return (
                        <div className='bg-white border rounded shadow p-2 text-center min-w-[80px]'>
                          <div className='font-bold'>{payload?.[0]?.name}</div>

                          <div className='font-semibold text-black'>
                            {formatted}
                          </div>
                        </div>
                      );
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className='flex flex-col mr-4 lg:mr-10 text-nowrap gap-2'>
              {monthlyDetails?.summary?.map((item, index) => (
                <div key={index} className='flex items-center gap-2'>
                  <div
                    className='w-5 h-3 rounded-full'
                    style={{
                      backgroundColor: COLORS[index % COLORS.length],
                    }}
                  ></div>

                  <div>{item?.name}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className='text-center mt-20 mb-24 w-full'>No Monthly Details</div>
      )}
    </>
  );
};
